// Pure logic for the LLM Rankings admin UI. AdminView.jsx is coverage-excluded
// (vite.config policy: large presentational shells are excluded, non-trivial
// logic is extracted + tested here). BYOM: local endpoints arrive from
// /api/llm/models keyed by provider_class (local_*), each model tagged
// { local: true, endpoint_id, endpoint_name } so a ranking can address a
// specific self-hosted / on-demand "model @ endpoint".

// Friendly provider label — local classes render as "Local / BYOM".
export function providerLabel(p) {
  if (!p) return p;
  return p.startsWith("local_") ? "Local / BYOM" : p.charAt(0).toUpperCase() + p.slice(1);
}

// Normalize a provider's model list to objects with an id (string entries → {id}).
export function normalizeModels(entries) {
  return (entries || [])
    .map((m) => (typeof m === "string" ? { id: m, kind: "chat" } : m))
    .filter((m) => m && m.id);
}

// Mutates the caller's cloud providerModels map to also include local (BYOM)
// providers, and returns { localKeys, chatProvidersAll } (cloud + local order).
export function addLocalProviders(dynamicModels, providerModels, chatProviders) {
  const dyn = dynamicModels || {};
  const localKeys = Object.keys(dyn).filter((k) => k.startsWith("local_"));
  for (const lk of localKeys) providerModels[lk] = normalizeModels(dyn[lk]);
  return { localKeys, chatProvidersAll: [...chatProviders, ...localKeys] };
}

// Returns a NEW entry with endpoint_id stamped from the selected model (local) or
// cleared (cloud). A local ranking entry MUST carry endpoint_id so the resolver
// can dispatch to the right per-tenant endpoint; a cloud entry never carries one.
export function withEndpointId(entry, modelObj) {
  const next = { ...entry };
  if (modelObj && modelObj.endpoint_id) next.endpoint_id = modelObj.endpoint_id;
  else delete next.endpoint_id;
  return next;
}

// Pure transform for updateRanking: change one field of one entry, re-defaulting
// the model (+ endpoint_id) when the provider changes, and re-stamping
// endpoint_id when the model changes. Returns a new rankings object.
export function applyRankingUpdate(rankings, fn, idx, field, value, lookup, filterByPurpose) {
  const next = { ...rankings };
  const list = [...(next[fn] || [])];
  let entry = { ...list[idx], [field]: value };
  if (field === "provider") {
    const filtered = filterByPurpose(lookup[value] || [], fn);
    entry.model = filtered[0] ? filtered[0].id : "";
    entry = withEndpointId(entry, filtered[0]);
  } else if (field === "model") {
    const mObj = (lookup[entry.provider] || []).find((m) => m.id === value);
    entry = withEndpointId(entry, mObj);
  }
  list[idx] = entry;
  next[fn] = list;
  return next;
}

// Pure transform for addRankingEntry: append a first-available entry (respecting
// the purpose filter), stamping endpoint_id for a local default. Caps at
// maxEntries (returns the input unchanged when full). Returns a new object.
export function appendRankingEntry(rankings, fn, providerOrder, lookup, filterByPurpose, maxEntries = 5) {
  const list = [...(rankings[fn] || [])];
  if (list.length >= maxEntries) return rankings;
  const provider = providerOrder.find((p) => filterByPurpose(lookup[p] || [], fn).length) || "";
  const firstModel = filterByPurpose(lookup[provider] || [], fn)[0];
  const entry = withEndpointId({ provider, model: firstModel ? firstModel.id : "" }, firstModel);
  list.push(entry);
  return { ...rankings, [fn]: list };
}

// Model option display label: "model @ endpoint" for a local endpoint, else "model".
export function modelOptionLabel(m) {
  if (!m) return "";
  return m.local && m.endpoint_name ? `${m.id} @ ${m.endpoint_name}` : m.id;
}
